import * as React from 'react';
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropDownList } from '@progress/kendo-react-dropdowns';

import { AutoComplete } from '@progress/kendo-react-dropdowns';
import { FloatingLabel } from '@progress/kendo-react-labels';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

import { CustomCommandCellGrid } from '../ButtonComponentsPanel/CustomCommandCellGrid';
import { ProcessError, ErrorCode } from '../../classes/ExceptionProcessor';
import { ListOfPaged, MinimumPage } from '../../classes/consts';

import FarmsService from "../../services/farms.service";

export default function FarmListComponents(props) {
    const { t } = useTranslation();
    // Error Hooks
    const [visibleError, setVisibleError] = React.useState(false);
    const [errorData, setErrorData] = React.useState({ messageError: "", code: "" });

    // Delete Event Hooks
    const [visibleRemove, setVisibleRemove] = React.useState(false);
    const [dataItemToRemove, setDataItemToRemove] = React.useState({});

    // Grid Hooks
    const [orderByString, setOrderByString] = React.useState("?order=name asc&filter=isInactive eq false");
    const [sortState, setSortState] = React.useState([]);
    const [pageState, setPageState] = React.useState({ skip: 0, take: MinimumPage() });

    // Loading Hooks
    const [loadingFarms, setLoadingFarms] = React.useState(true);
    const closeError = React.useCallback(() => { setVisibleError(false) }, []);

    const [data, setData] = React.useState([]);

    //#region DataFilters
    //#region FarmName Filter
    const [farmNameFilter, setFarmNameFilter] = React.useState("");

    const handleFarmNameFilterChange = (event) => {
        const value = event.value || "";
        setFarmNameFilter(value);
    };
    //#endregion
    //#region OfficialMark Filter
    const [officialMarkFilter, setOfficialMarkFilter] = React.useState("");

    const handleOfficialMarkFilterChange = (event) => {
        const value = event.value || "";
        setOfficialMarkFilter(value);
    };
    //#endregion
    //#region RegaCode Filter
    const [regaCodeFilter, setRegaCodeFilter] = React.useState("");

    const handleRegaCodeFilterChange = (event) => {
        const value = event.value || "";
        setRegaCodeFilter(value);
    };
    //#endregion
    //#region City Filter
    const [cityFilter, setCityFilter] = React.useState("");

    const handleCityFilterChange = (event) => {
        const value = event.value || "";
        setCityFilter(value);
    };
    //#endregion
    //#region IsInactive Filter
    const statusOptions = [
        { key: "active", label: t("Farms_Status_Active") },
        { key: "inactive", label: t("Farms_Status_Inactive") },
        { key: "all", label: t("Farms_Status_All") }
    ];
    const [isInactiveFilter, setIsInactiveFilter] = React.useState("active");

    const handleStatusChange = React.useCallback((e) => {
        let selected;
        if (e?.value !== undefined) selected = e.value;
        else if (e?.target && e.target.value !== undefined) selected = e.target.value;
        else if (e?.target && e.target.dataItem !== undefined) selected = e.target.dataItem;

        const key = selected?.key ?? selected ?? "all";
        setIsInactiveFilter(key);
    }, []);

    const applySortingAndFiltering = React.useCallback((sortOverride) => {
        const sort = sortOverride || sortState;

        const sortField = sort?.[0]?.field || "name";
        const sortDir = sort?.[0]?.dir || "asc";
        const order = `${sortField} ${sortDir}`;

        const filterParts = [];

        if (farmNameFilter) filterParts.push(`contains(name,'${farmNameFilter}')`);
        if (officialMarkFilter) filterParts.push(`contains(officialMark,'${officialMarkFilter}')`);
        if (regaCodeFilter) filterParts.push(`contains(regaCode,'${regaCodeFilter}')`);
        if (cityFilter) filterParts.push(`contains(city,'${cityFilter}')`);

        if (isInactiveFilter === "active") filterParts.push("isInactive eq false");
        else if (isInactiveFilter === "inactive") filterParts.push("isInactive eq true");

        const filter = filterParts.join(" and ");
        const queryString = `?order=${encodeURIComponent(order)}${filter ? `&filter=${encodeURIComponent(filter)}` : ""}`;

        setOrderByString(queryString);
    }, [sortState, farmNameFilter, officialMarkFilter, regaCodeFilter, cityFilter, isInactiveFilter]);
    //#endregion
    //#endregion

    React.useEffect(() => {
    setLoadingFarms(true);

    FarmsService.getFilteredFarms(orderByString)
        .then(response => response.data)
        .then(json => {
            setData(json.contract || []);
            setLoadingFarms(false);
        }).catch(err => {
            setErrorData({ messageError: ProcessError(err), code: ErrorCode(err) });
            setVisibleError(true);
            setLoadingFarms(false);
        });
    }, [orderByString]);

    const CommandCell = React.useCallback(cellProps => (
        <CustomCommandCellGrid
            {...cellProps}
            editField="inEdit"
            remove={remove}
            linkToDetails={'/Farms/' + cellProps.dataItem.farmId + '/Details'}
            isAdmin={props.isAdmin}
        />
    ), [props]);

    const remove = (dataItem) => {
        setDataItemToRemove(dataItem);
        setVisibleRemove(true);
    }

    const removeDataItem = async () => {
        FarmsService.deleteFarm(dataItemToRemove.farmId)
            .then(() => {
                setDataItemToRemove({});
                setVisibleRemove(false);
            }).catch(err => {
                setDataItemToRemove({});
                setVisibleError(true);
                setVisibleRemove(false);
                setErrorData({ messageError: ProcessError(err), code: ErrorCode(err) })
            });
    }

    const loadingPanel =
        <div className="k-loading-mask">
            <span className="k-loading-text">Loading</span>
            <div className="k-loading-image"></div>
            <div className="k-loading-color"></div>
        </div>;

    const loadingCell = React.useCallback((tdElement, cellProps) => {
        if (cellProps.dataItem[cellProps.field] === undefined) {
            // shows loading cell if no data
            return <td> <span className="k-placeholder-line"></span></td>;
        }

        // default rendering for this cell
        return tdElement;
    }, []);

    const sortChange = React.useCallback((event) => {
        setSortState(event.sort);
        applySortingAndFiltering(event.sort);
    });

    const pageChange = React.useCallback((event) => {
        setPageState({ ...pageState, skip: event.page.skip, take: event.page.take });
    }, [pageState]);

    const StatusCell = (props) => {
        const { dataItem } = props;
        const isActive = !dataItem.isInactive;

        return (
            <td style={{ textAlign: "center" }}>
                <span className={`k-icon ${isActive ? "k-i-check" : "k-i-close"}`} />
            </td>
        );
    };

    const processedData = (data.length > 0 || farmNameFilter || officialMarkFilter || regaCodeFilter || cityFilter
        ? data
        : data).slice(pageState.skip, pageState.skip + pageState.take);

    return (
        <div>
            {visibleError && (
                <Dialog title={"Error " + errorData.code} onClose={closeError}>
                    <p style={{ margin: "25px", textAlign: "center" }}>
                        <b>{errorData.messageError}</b>
                    </p>
                    <DialogActionsBar>
                        <button className="k-button" onClick={() => { setVisibleError(false) }}>
                            OK
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {loadingFarms && loadingPanel}

            {visibleRemove && (
                <Dialog title={t("Global_RemoveDialog_Title")} onClose={closeError}>
                    <p style={{ margin: "25px", textAlign: "center" }}>
                        <b>{t("Global_RemoveDialog_Text") + " \"" + dataItemToRemove.name + "\" ?"} </b>
                    </p>
                    <DialogActionsBar>
                        <button className="k-button" onClick={() => removeDataItem()}>
                            {t("Global_OKButton_Text")}
                        </button>
                        <button className="k-button" onClick={() => { setVisibleRemove(false) }}>
                            {t("Global_CancelButton_Text")}
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <div className="container-fluid">
                <div className="card">
                    <div className="card-header m-0 p-3">
                        <div className="row g-3 w-100 align-items-end">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("Farms_Status_Label")} editorId="estadoInput">
                                    <DropDownList
                                        id="estadoInput"
                                        loading={loadingFarms}
                                        style={{ width: '100%' }}
                                        data={statusOptions}
                                        textField="label"
                                        dataItemKey="key"
                                        value={statusOptions.find(opt => opt.key === isInactiveFilter)}
                                        onChange={handleStatusChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("Farms_Status_FarmName")} editorId="nombreInput">
                                    <AutoComplete
                                        id="nombreInput"
                                        loading={loadingFarms}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(farm => farm.name))]}
                                        value={farmNameFilter}
                                        onChange={handleFarmNameFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("Farms_Status_OfficialMark")} editorId="officialMarkInput">
                                    <AutoComplete
                                        id="officialMarkInput"
                                        loading={loadingFarms}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(farm => farm.officialMark))]}
                                        value={officialMarkFilter}
                                        onChange={handleOfficialMarkFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("Farms_Status_RegaCode")} editorId="regaCodeInput">
                                    <AutoComplete
                                        id="regaCodeInput"
                                        loading={loadingFarms}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(farm => farm.regaCode))]}
                                        value={regaCodeFilter}
                                        onChange={handleRegaCodeFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <FloatingLabel label={t("Farms_Status_City")} editorId="cityInput">
                                    <AutoComplete
                                        id="cityInput"
                                        loading={loadingFarms}
                                        style={{ width: '100%' }}
                                        data={[...new Set(data.map(farm => farm.city))]}
                                        value={cityFilter}
                                        onChange={handleCityFilterChange}
                                    />
                                </FloatingLabel>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-2">
                                <button
                                    className="k-button k-primary w-100"
                                    style={{ marginTop: '25px' }}
                                    onClick={() => applySortingAndFiltering()}
                                    title={t("Farms_Button_ApplySorting")}
                                >
                                    {t("Farms_Button_ApplySorting")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <br />

                <div className="table-responsive">
                    <Grid
                        cellRender={loadingCell}
                        pageable={{ pageSizes: ListOfPaged() }}
                        data={processedData}
                        skip={pageState.skip}
                        take={pageState.take}
                        total={
                            (data.length > 0 ||
                                farmNameFilter ||
                                officialMarkFilter ||
                                regaCodeFilter ||
                                cityFilter
                                ? data.length
                                : data.length)
                        }
                        scrollable={'none'}
                        sortable={{ allowUnsort: true, mode: "single" }}
                        sort={sortState}
                        onSortChange={sortChange}
                        onPageChange={pageChange}
                    >
                        <GridToolbar>
                            <div>
                                <Link to={'/Farms/Create'}>
                                    <button title={t("Global_Label_Text_Create")} className="k-button k-primary">
                                        &nbsp;<span className="k-icon k-i-plus-circle" />&nbsp;
                                        {t("Global_Label_Text_Create")} &nbsp;
                                    </button>
                                </Link>
                            </div>
                        </GridToolbar>

                        <Column cell={CommandCell} width={"130px"} />
                        <Column title={t("Farms_Active_Label")} cell={StatusCell} width="65px" sortable={false} />
                        <Column field="farmId" title={t("Farms_Status_Identificator")} sortable={true} />
                        <Column field="name" title={t("Farms_Status_FarmName")} sortable={true} />
                        <Column field="officialMark" title={t("Farms_Status_OfficialMark")} sortable={false} />
                        <Column field="regaCode" title={t("Farms_Status_RegaCode")} sortable={false} />
                        <Column field="city" title={t("Farms_Status_City")} sortable={false} />
                        <Column field="integratorId" title={t("Farms_Status_Integrator")} sortable={false} />
                    </Grid>
                </div>
            </div>
        </div>
    );
}